import { Locale } from "../Locale.ts";

/**
 * UI strings for the analytics consent banner and the control on /privacy —
 * ADR 0051 part 2, ADR 0025 amendment 2 §H.
 *
 * These are the strings least allowed to fall back to English. A visitor who
 * cannot read the question cannot answer it, and an answer that was not
 * understood is not consent - so a missing translation here is not a cosmetic
 * gap the way a missing button label elsewhere would be. The `satisfies` guard
 * below is what makes that a build error.
 *
 * The wording avoids "we value your privacy" and the rest of the genre. It
 * says what the cookies do, who gets the data, and that saying no costs the
 * visitor nothing - which on this site is true, because analytics feeds the
 * roadmap and nothing else.
 */
const en = {
    "consent.message":
        "Math Pages uses Google Analytics to count visits and see which worksheets get used. It sets cookies and sends Google your IP address. Nothing else about you is collected, and the site works exactly the same either way.",
    "consent.accept": "Allow analytics",
    "consent.reject": "No thanks",
    "consent.learnMore": "Privacy policy",
    "consent.ariaLabel": "Analytics cookies",
    "consent.settingsHeading": "Analytics cookies",
    "consent.statusGranted": "You currently allow Google Analytics on this site.",
    "consent.statusDenied": "You currently do not allow Google Analytics on this site.",
    "consent.statusUndecided": "You have not chosen yet, so Google Analytics is not running.",
    "consent.saved": "Saved.",
} as const;

export type ConsentStringKey = keyof typeof en;

const es = {
    "consent.message":
        "Math Pages usa Google Analytics para contar las visitas y ver qué fichas se utilizan. Instala cookies y envía tu dirección IP a Google. No se recoge nada más sobre ti, y el sitio funciona igual elijas lo que elijas.",
    "consent.accept": "Permitir análisis",
    "consent.reject": "No, gracias",
    "consent.learnMore": "Política de privacidad",
    "consent.ariaLabel": "Cookies de análisis",
    "consent.settingsHeading": "Cookies de análisis",
    "consent.statusGranted": "Actualmente permites Google Analytics en este sitio.",
    "consent.statusDenied": "Actualmente no permites Google Analytics en este sitio.",
    "consent.statusUndecided": "Todavía no has elegido, así que Google Analytics no está activo.",
    "consent.saved": "Guardado.",
} satisfies Record<ConsentStringKey, string>;

const fr = {
    "consent.message":
        "Math Pages utilise Google Analytics pour compter les visites et voir quelles fiches sont utilisées. Cela dépose des cookies et transmet votre adresse IP à Google. Rien d'autre n'est collecté à votre sujet, et le site fonctionne de la même façon dans les deux cas.",
    "consent.accept": "Autoriser les statistiques",
    "consent.reject": "Non merci",
    "consent.learnMore": "Politique de confidentialité",
    "consent.ariaLabel": "Cookies de mesure d'audience",
    "consent.settingsHeading": "Cookies de mesure d'audience",
    "consent.statusGranted": "Vous autorisez actuellement Google Analytics sur ce site.",
    "consent.statusDenied": "Vous n'autorisez pas Google Analytics sur ce site.",
    "consent.statusUndecided": "Vous n'avez pas encore choisi, donc Google Analytics n'est pas actif.",
    "consent.saved": "Enregistré.",
} satisfies Record<ConsentStringKey, string>;

export const consentStrings: Record<Locale, Record<ConsentStringKey, string>> = { en, es, fr };
